import { reactive } from 'vue'
import { parseAndRoundNumberInput } from '../shared/numberInput'

/** Scales every px length in a CSS shadow (rounded to 3 decimals, tiny values to 0). */
export function scaleShadowString(shadow: string, scale: number): string {
  if (!shadow) return shadow
  const trimmed = shadow.trim()
  if (!trimmed || trimmed === 'none') return trimmed
  if (!Number.isFinite(scale) || scale <= 0) return trimmed
  return trimmed.replace(/(-?\d+(?:\.\d+)?)px/g, (_, num) => {
    const n = Number(num)
    if (!Number.isFinite(n)) return `${num}px`
    const out = n * scale
    const rounded = Math.abs(out) < 0.0005 ? 0 : Math.round(out * 1000) / 1000
    return `${rounded}px`
  })
}

/** The shadow a deck gets when its shadow section is switched on. */
export const DEFAULT_SHADOW = {
  x: 0,
  y: 14,
  blur: 34,
  spread: 0,
  color: 'rgba(15, 23, 42, 0.18)',
} as const

export type ShadowParts = { x: number; y: number; blur: number; spread: number; color: string }

export type ShadowParseResult =
  { kind: 'unset' } | ({ kind: 'parsed' } & ShadowParts) | { kind: 'raw'; raw: string }

/**
 * Reads "Xpx Ypx BLURpx SPREADpx color" into its parts; other strings stay raw. Empty
 * values and the legacy "none" count as unset.
 */
export function parseShadow(value: unknown): ShadowParseResult {
  if (typeof value !== 'string') return { kind: 'unset' }
  const raw = value.trim()
  if (!raw.length) return { kind: 'unset' }
  if (raw.toLowerCase() === 'none') return { kind: 'unset' }
  const m = raw.match(
    /^(-?\d+(?:\.\d+)?)px\s+(-?\d+(?:\.\d+)?)px\s+(\d+(?:\.\d+)?)px\s+(-?\d+(?:\.\d+)?)px\s*(.*)$/,
  )
  if (!m) return { kind: 'raw', raw }
  const x = Number(m[1])
  const y = Number(m[2])
  const blur = Number(m[3])
  const spread = Number(m[4])
  const color = (m[5] || '').trim() || DEFAULT_SHADOW.color
  if (![x, y, blur, spread].every((n) => Number.isFinite(n))) return { kind: 'raw', raw }
  return { kind: 'parsed', x, y, blur, spread, color }
}

export const buildShadow = (input: ShadowParts) =>
  `${input.x}px ${input.y}px ${input.blur}px ${input.spread}px ${
    input.color?.trim().length ? input.color.trim() : DEFAULT_SHADOW.color
  }`

/**
 * Form state for editing a shadow by its parts (or as a raw string). `commit` writes
 * the form back to the settings; deck editor and template form commit differently.
 */
export function createShadowForm(commit: () => void) {
  const shadowForm = reactive({
    mode: 'unset' as ShadowParseResult['kind'],
    x: DEFAULT_SHADOW.x as number,
    y: DEFAULT_SHADOW.y as number,
    blur: DEFAULT_SHADOW.blur as number,
    spread: DEFAULT_SHADOW.spread as number,
    color: DEFAULT_SHADOW.color as string,
    raw: '',
  })

  const resetToDefault = () => {
    shadowForm.x = DEFAULT_SHADOW.x
    shadowForm.y = DEFAULT_SHADOW.y
    shadowForm.blur = DEFAULT_SHADOW.blur
    shadowForm.spread = DEFAULT_SHADOW.spread
    shadowForm.color = DEFAULT_SHADOW.color
  }

  const syncShadowFormFromTemplate = (value: unknown) => {
    const parsed = parseShadow(value)
    shadowForm.mode = parsed.kind
    if (parsed.kind === 'unset') {
      resetToDefault()
      shadowForm.raw = ''
      return
    }
    if (parsed.kind === 'raw') {
      shadowForm.raw = parsed.raw
      return
    }
    shadowForm.x = parsed.x
    shadowForm.y = parsed.y
    shadowForm.blur = parsed.blur
    shadowForm.spread = parsed.spread
    shadowForm.color = parsed.color
    shadowForm.raw = buildShadow(parsed)
  }

  const formShadow = () =>
    buildShadow({
      x: shadowForm.x,
      y: shadowForm.y,
      blur: shadowForm.blur,
      spread: shadowForm.spread,
      color: shadowForm.color,
    })

  /** A typed offset/blur/spread (clamped) switches the form to its parts and commits. */
  const setShadowNumber = (
    key: 'x' | 'y' | 'blur' | 'spread',
    event: Event,
    { min, max }: { min: number; max: number },
  ) => {
    const n = parseAndRoundNumberInput((event.target as HTMLInputElement).value, 3)
    if (n === undefined) return
    shadowForm.mode = 'parsed'
    shadowForm[key] = Math.max(min, Math.min(max, n))
    commit()
  }

  return { shadowForm, resetToDefault, syncShadowFormFromTemplate, formShadow, setShadowNumber }
}
